import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import AdminDashboard from './components/AdminDashboard';
import CustomerDashboard from './components/CustomerDashboard';
import EnhancedHandymanApp from './components/EnhancedHandymanApp';
import Login from './components/Login';
import HandymanLogin from './components/HandymanLogin';
import UnifiedLogin from './components/UnifiedLogin';
import PublicLeadForm from './components/PublicLeadForm';
import PublicPackagePurchase from './components/PublicPackagePurchase';
import PublicInvoicePayment from './components/PublicInvoicePayment';
import QuoteActionPage from './QuoteActionPage';
import AppInstallPage from './AppInstallPage';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor to use appropriate token
axiosInstance.interceptors.request.use((config) => {
  // Check for PWA token first, then handyman, then general token
  const pwaToken = localStorage.getItem('pwa_token');
  const handymanToken = localStorage.getItem('handyman_token');
  const token = localStorage.getItem('token');
  
  if (pwaToken) {
    config.headers.Authorization = `Bearer ${pwaToken}`;
  } else if (handymanToken) {
    config.headers.Authorization = `Bearer ${handymanToken}`;
  } else if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Register service worker globally (required for PWA install prompt on all routes)
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  newWorker.postMessage({ type: 'SKIP_WAITING' });
                }
              });
            }
          });
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    // Check for PWA persistent login first
    const pwaToken = localStorage.getItem('pwa_token');
    const pwaUser = localStorage.getItem('pwa_user');
    
    if (pwaToken && pwaUser) {
      setUser(JSON.parse(pwaUser));
      setLoading(false);
      return;
    }
    
    // Check for handyman persistent login
    const handymanToken = localStorage.getItem('handyman_token');
    const handymanUser = localStorage.getItem('handyman_user');
    
    if (handymanToken && handymanUser) {
      setUser(JSON.parse(handymanUser));
      setLoading(false);
      return;
    }
    
    // Then check regular login
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    // Store PWA token for unified login
    localStorage.setItem('pwa_token', token);
    localStorage.setItem('pwa_user', JSON.stringify(userData));
    
    // Also store in role-specific keys for backward compatibility
    if (userData.role === 'handyman') {
      localStorage.setItem('handyman_token', token);
      localStorage.setItem('handyman_user', JSON.stringify(userData));
    }
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    setUser(userData);
  };

  const handleLogout = () => {
    // Clear all tokens
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('handyman_token');
    localStorage.removeItem('handyman_user');
    localStorage.removeItem('pwa_token');
    localStorage.removeItem('pwa_user');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Lead Form Route - No Auth Required */}
          <Route path="/lead-form/:formId" element={<PublicLeadForm />} />
          
          {/* Public Package Purchase Route - No Auth Required */}
          <Route path="/purchase/package/:packageId" element={<PublicPackagePurchase />} />
          <Route path="/pay/invoice" element={<PublicInvoicePayment />} />
          
          <Route
            path="/"
            element={
              user ? (
                user.role === 'super_admin' || user.role === 'admin' || user.role === 'manager' || user.role === 'supervisor' || user.role === 'marketing' ? (
                  <Navigate to="/admin" replace />
                ) : user.role === 'customer' ? (
                  <Navigate to="/customer" replace />
                ) : user.role === 'handyman' ? (
                  <Navigate to="/handyman" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/login"
            element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" replace />}
          />
          {/* Unified PWA Login - Admin, Handyman, Customer */}
          <Route
            path="/app"
            element={
              !user ? (
                <UnifiedLogin onLogin={handleLogin} />
              ) : user.role === 'admin' || user.role === 'super_admin' || user.role === 'manager' || user.role === 'supervisor' || user.role === 'marketing' ? (
                <Navigate to="/admin" replace />
              ) : user.role === 'handyman' ? (
                <Navigate to="/handyman" replace />
              ) : user.role === 'customer' ? (
                <Navigate to="/customer" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* Dedicated Handyman PWA Login - only handyman option */}
          <Route
            path="/handyman-login"
            element={
              !user ? (
                <HandymanLogin onLogin={handleLogin} />
              ) : user.role === 'handyman' ? (
                <Navigate to="/handyman" replace />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/*"
            element={
              user && (user.role === 'super_admin' || user.role === 'admin' || user.role === 'manager' || user.role === 'supervisor' || user.role === 'marketing') ? (
                <AdminDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/customer/*"
            element={
              user && user.role === 'customer' ? (
                <CustomerDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/handyman/*"
            element={
              user && user.role === 'handyman' ? (
                <EnhancedHandymanApp user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/handyman-login" replace />
              )
            }
          />
          {/* Public Quote Action Page - No auth required, token-based */}
          <Route path="/quote-action/:quoteId" element={<QuoteActionPage />} />
          
          {/* PWA Install Page - No auth required */}
          <Route path="/APPS" element={<AppInstallPage />} />
          <Route path="/apps" element={<AppInstallPage />} />
          <Route path="/install" element={<AppInstallPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;